import React from "react";

import type { SipRegistrationStatus } from "~/services/calling/types";

type SipTabMessage =
  | {
      type: "leader-state";
      tabId: string;
      sipStatus: SipRegistrationStatus;
    }
  | {
      type: "focus-leader";
      tabId: string;
    };

type UseSipTabCoordinatorParams = {
  enabled: boolean;
  scope: string | null | undefined;
  sipStatus: SipRegistrationStatus;
};

type SipTabCoordinator = {
  isLeader: boolean;
  canRequestLeader: boolean;
  requestLeaderFocus: () => void;
};

const LOCK_RETRY_MS = 2000;
const LEADER_HEARTBEAT_MS = 5000;
const LOCK_RELEASE_GRACE_MS = 3500;

const createTabId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const isSipTabMessage = (value: unknown): value is SipTabMessage => {
  if (!value || typeof value !== "object") return false;

  const message = value as Partial<SipTabMessage>;
  return (
    (message.type === "leader-state" || message.type === "focus-leader") &&
    typeof message.tabId === "string"
  );
};

export const useSipTabCoordinator = ({
  enabled,
  scope,
  sipStatus,
}: UseSipTabCoordinatorParams): SipTabCoordinator => {
  const tabIdRef = React.useRef(createTabId());
  const channelRef = React.useRef<BroadcastChannel | null>(null);
  const releaseLockRef = React.useRef<(() => void) | null>(null);
  const lockRequestActiveRef = React.useRef(false);
  const isLeaderRef = React.useRef(false);
  const sipStatusRef = React.useRef(sipStatus);
  const [isLeader, setIsLeader] = React.useState(false);
  const [hasLeader, setHasLeader] = React.useState(false);
  const [leaderSipStatus, setLeaderSipStatus] =
    React.useState<SipRegistrationStatus | null>(null);

  sipStatusRef.current = sipStatus;

  const publishLeaderState = React.useCallback(() => {
    if (!isLeaderRef.current) return;

    channelRef.current?.postMessage({
      type: "leader-state",
      tabId: tabIdRef.current,
      sipStatus: sipStatusRef.current,
    } satisfies SipTabMessage);
  }, []);

  React.useEffect(() => {
    publishLeaderState();
  }, [publishLeaderState, sipStatus]);

  React.useEffect(() => {
    if (!enabled || !scope || typeof window === "undefined") {
      isLeaderRef.current = false;
      setIsLeader(false);
      setHasLeader(false);
      setLeaderSipStatus(null);
      return;
    }

    isLeaderRef.current = false;
    setIsLeader(false);

    const tabId = tabIdRef.current;
    const lockName = `aidcall:sip-owner:${scope}`;
    const channelName = `aidcall:sip-owner:${scope}`;
    let active = true;
    let retryTimer: number | null = null;
    let heartbeatTimer: number | null = null;

    const setLeadership = (nextIsLeader: boolean) => {
      if (!active) return;

      isLeaderRef.current = nextIsLeader;
      setIsLeader(nextIsLeader);
      setHasLeader((previousHasLeader) => nextIsLeader || previousHasLeader);

      if (nextIsLeader) {
        setLeaderSipStatus(sipStatusRef.current);
        window.setTimeout(publishLeaderState, 0);
      }
    };

    const channel =
      typeof BroadcastChannel === "function"
        ? new BroadcastChannel(channelName)
        : null;
    channelRef.current = channel;

    if (channel) {
      channel.onmessage = (event: MessageEvent<unknown>) => {
        if (!isSipTabMessage(event.data) || event.data.tabId === tabId) return;

        if (event.data.type === "leader-state") {
          setHasLeader(true);
          setLeaderSipStatus(event.data.sipStatus);
          return;
        }

        if (event.data.type === "focus-leader" && isLeaderRef.current) {
          window.focus();
        }
      };
    }

    const lockManager = navigator.locks;
    const supportsWebLocks = typeof lockManager?.request === "function";

    if (!supportsWebLocks) {
      setHasLeader(false);
      setLeaderSipStatus(null);
      return () => {
        channel?.close();
        channelRef.current = null;
      };
    }

    const tryAcquireWebLock = () => {
      if (!active || lockRequestActiveRef.current) return;

      lockRequestActiveRef.current = true;
      void lockManager
        .request(lockName, { ifAvailable: true }, async (lock) => {
          if (!active) return;

          if (!lock) {
            setLeadership(false);
            setHasLeader(true);
            return;
          }

          setLeadership(true);
          await new Promise<void>((resolve) => {
            releaseLockRef.current = resolve;
          });
          releaseLockRef.current = null;
        })
        .catch(() => {
          if (active) {
            setLeadership(false);
            setHasLeader(false);
          }
        })
        .finally(() => {
          lockRequestActiveRef.current = false;
          if (active && isLeaderRef.current) {
            setLeadership(false);
          }
        });
    };

    tryAcquireWebLock();
    retryTimer = window.setInterval(tryAcquireWebLock, LOCK_RETRY_MS);

    heartbeatTimer = window.setInterval(() => {
      if (!isLeaderRef.current) return;

      publishLeaderState();
    }, LEADER_HEARTBEAT_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;

      tryAcquireWebLock();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      active = false;
      if (retryTimer !== null) window.clearInterval(retryTimer);
      if (heartbeatTimer !== null) window.clearInterval(heartbeatTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      channel?.close();
      channelRef.current = null;
      const releaseLock = releaseLockRef.current;
      releaseLockRef.current = null;
      if (releaseLock && isLeaderRef.current) {
        window.setTimeout(releaseLock, LOCK_RELEASE_GRACE_MS);
      } else {
        releaseLock?.();
      }
      isLeaderRef.current = false;
      lockRequestActiveRef.current = false;
    };
  }, [enabled, publishLeaderState, scope]);

  const requestLeaderFocus = React.useCallback(() => {
    if (isLeaderRef.current) {
      window.focus();
      return;
    }

    channelRef.current?.postMessage({
      type: "focus-leader",
      tabId: tabIdRef.current,
    } satisfies SipTabMessage);
  }, []);

  return {
    isLeader,
    canRequestLeader:
      isLeader ||
      (hasLeader &&
        leaderSipStatus === "registered" &&
        channelRef.current !== null),
    requestLeaderFocus,
  };
};
